import { confirmDialog, showDialog } from '@/utils/dialogs';
/**
 * InvitePersonModal — unified invite modal with 4 role tabs (Öğrenci /
 * Mentör / Yönetici / Fatura yetkilisi). Replaces the 4 standalone invite
 * modals (StudentInviteModalEnhanced, MentorInviteModal, AdminInviteModal,
 * AdminBillInviteModalEnhanced). Layout follows the "Kişi davet et"
 * archetype: header → role tab bar → two-col form (form on left,
 * capability sidebar on right) → footer.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { ADMIN_COLORS } from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { mentorAPI } from '@/services/api';
import { withInviteWarning } from './inviteWarnings';
import { useTranslation } from '../../hooks/useTranslation';
import {
  createCheckoutError,
  showCheckoutFailureDialog,
} from '../../utils/checkoutFailures';

const CHECKOUT_SURFACE = 'invite_person_billing';

export type InviteRole = 'student' | 'mentor' | 'admin' | 'billing';

interface RoleConfig {
  key: InviteRole;
  label: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  capabilityIcon: keyof typeof MaterialIcons.glyphMap;
  capabilityTitle: string;
  capabilities: string[];
  roleHintTitle: string;
  roleHintBody: string;
  passwordEnabled: boolean;
  submitIcon: keyof typeof MaterialIcons.glyphMap;
  submitLabel: string;
  submittingLabel: string;
  successTitle: string;
  successMessage: string;
  errorTitle: string;
  errorMessage: string;
  footerHint: string;
}

interface InvitePersonModalProps {
  visible: boolean;
  onClose: () => void;
  schoolId: number;
  /** Optional: pre-select a role tab. Defaults to 'student'. */
  initialRole?: InviteRole;
  /** Optional: limit which role tabs are visible (e.g. school admins
   *  only see student/mentor; platform admins see all four). */
  allowedRoles?: ReadonlyArray<InviteRole>;
  /** Called after a successful invite (any role). */
  onInvited?: (role: InviteRole, response?: any) => void;
}

const validateEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const DEFAULT_ROLES: ReadonlyArray<InviteRole> = ['student', 'mentor', 'admin', 'billing'];

export default function InvitePersonModal({
  visible,
  onClose,
  schoolId,
  initialRole = 'student',
  allowedRoles = DEFAULT_ROLES,
  onInvited,
}: InvitePersonModalProps) {
  const { t } = useTranslation();
  const [role, setRole] = useState<InviteRole>(initialRole);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pwdSecure, setPwdSecure] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailFocused, setEmailFocused] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const [pwdFocused, setPwdFocused] = useState(false);

  const openBillingCheckout = async (url: string) => {
    try {
      const opened = window.open(url, '_blank');
      if (!opened) {
        throw createCheckoutError('popup_blocked');
      }
      opened.opener = null;
    } catch (checkoutError) {
      await showCheckoutFailureDialog({
        error: checkoutError,
        surface: CHECKOUT_SURFACE,
        checkoutKey: String(schoolId),
        properties: {
          product_type: 'school_subscription',
          school_id: schoolId,
        },
        retry: () => openBillingCheckout(url),
        t,
      });
    }
  };

  useEffect(() => {
    if (visible) {
      setRole(initialRole);
      setFullName('');
      setEmail('');
      setPassword('');
      setShowPassword(false);
      setPwdSecure(true);
      setError(null);
    }
  }, [visible, initialRole]);

  const ROLE_CONFIGS: Record<InviteRole, RoleConfig> = useMemo(
    () => ({
      student: {
        key: 'student',
        label: t('invitePerson.tabs.student'),
        icon: 'school',
        capabilityIcon: 'school',
        capabilityTitle: t(
          'invitePerson.student.capabilityTitle',
        ),
        capabilities: [
          t('invitePerson.student.cap1'),
          t('invitePerson.student.cap2'),
          t('invitePerson.student.cap3'),
        ],
        roleHintTitle: t('invitePerson.student.hintTitle'),
        roleHintBody: t(
          'invitePerson.student.hintBody',
        ),
        passwordEnabled: false,
        submitIcon: 'send',
        submitLabel: t('invitePerson.student.submit'),
        submittingLabel: t('invitePerson.student.submitting'),
        successTitle: t('invitePerson.student.successTitle'),
        successMessage: t(
          'invitePerson.student.successMessage',
        ),
        errorTitle: t('invitePerson.errorTitle'),
        errorMessage: t(
          'invitePerson.student.errorMessage',
        ),
        footerHint: t(
          'invitePerson.student.footerHint',
        ),
      },
      mentor: {
        key: 'mentor',
        label: t('invitePerson.tabs.mentor'),
        icon: 'psychology',
        capabilityIcon: 'psychology',
        capabilityTitle: t('invitePerson.mentor.capabilityTitle'),
        capabilities: [
          t('invitePerson.mentor.cap1'),
          t('invitePerson.mentor.cap2'),
          t('invitePerson.mentor.cap3'),
        ],
        roleHintTitle: t('invitePerson.mentor.hintTitle'),
        roleHintBody: t(
          'invitePerson.mentor.hintBody',
        ),
        passwordEnabled: false,
        submitIcon: 'send',
        submitLabel: t('invitePerson.mentor.submit'),
        submittingLabel: t('invitePerson.mentor.submitting'),
        successTitle: t('invitePerson.mentor.successTitle'),
        successMessage: t(
          'invitePerson.mentor.successMessage',
        ),
        errorTitle: t('invitePerson.errorTitle'),
        errorMessage: t(
          'invitePerson.mentor.errorMessage',
        ),
        footerHint: t(
          'invitePerson.mentor.footerHint',
        ),
      },
      admin: {
        key: 'admin',
        label: t('invitePerson.tabs.admin'),
        icon: 'admin-panel-settings',
        capabilityIcon: 'admin-panel-settings',
        capabilityTitle: t('invitePerson.admin.capabilityTitle'),
        capabilities: [
          t('invitePerson.admin.cap1'),
          t('invitePerson.admin.cap2'),
          t('invitePerson.admin.cap3'),
        ],
        roleHintTitle: t('invitePerson.admin.hintTitle'),
        roleHintBody: t(
          'invitePerson.admin.hintBody',
        ),
        passwordEnabled: true,
        submitIcon: 'person-add',
        submitLabel: t('invitePerson.admin.submit'),
        submittingLabel: t('invitePerson.admin.submitting'),
        successTitle: t('invitePerson.admin.successTitle'),
        successMessage: t(
          'invitePerson.admin.successMessage',
        ),
        errorTitle: t('invitePerson.errorTitle'),
        errorMessage: t(
          'invitePerson.admin.errorMessage',
        ),
        footerHint: t(
          'invitePerson.admin.footerHint',
        ),
      },
      billing: {
        key: 'billing',
        label: t('invitePerson.tabs.billing'),
        icon: 'receipt-long',
        capabilityIcon: 'receipt-long',
        capabilityTitle: t('invitePerson.billing.capabilityTitle'),
        capabilities: [
          t('invitePerson.billing.cap1'),
          t('invitePerson.billing.cap2'),
          t('invitePerson.billing.cap3'),
        ],
        roleHintTitle: t('invitePerson.billing.hintTitle'),
        roleHintBody: t(
          'invitePerson.billing.hintBody',
        ),
        passwordEnabled: true,
        submitIcon: 'add',
        submitLabel: t('invitePerson.billing.submit'),
        submittingLabel: t('invitePerson.billing.submitting'),
        successTitle: t('invitePerson.billing.successTitle'),
        successMessage: t(
          'invitePerson.billing.successMessage',
        ),
        errorTitle: t('invitePerson.errorTitle'),
        errorMessage: t(
          'invitePerson.billing.errorMessage',
        ),
        footerHint: t(
          'invitePerson.billing.footerHint',
        ),
      },
    }),
    [t],
  );

  const visibleRoles = (
    allowedRoles && allowedRoles.length > 0 ? allowedRoles : DEFAULT_ROLES
  ).filter((r) => DEFAULT_ROLES.includes(r));
  const config = ROLE_CONFIGS[role] ?? ROLE_CONFIGS.student;
  const emailValid = email.trim().length === 0 || validateEmail(email);

  const submit = async () => {
    setError(null);
    if (!fullName.trim()) {
      setError(t('invitePerson.validation.nameRequired'));
      return;
    }
    if (!email.trim() || !validateEmail(email)) {
      setError(t('invitePerson.validation.emailInvalid'));
      return;
    }

    try {
      setLoading(true);
      const trimmedName = fullName.trim();
      const trimmedEmail = email.trim();
      const optionalPwd = config.passwordEnabled ? password || undefined : undefined;
      let response: any = null;

      if (role === 'student') {
        response = await apiRequest({
          endpoint: `/schools/${schoolId}/students/invite`,
          method: 'POST',
          data: { email: trimmedEmail, full_name: trimmedName },
        });
      } else if (role === 'mentor') {
        response = await mentorAPI.inviteMentor(schoolId, trimmedEmail, trimmedName);
      } else if (role === 'admin') {
        response = await apiRequest({
          endpoint: `/schools/${schoolId}/admins`,
          method: 'POST',
          data: {
            email: trimmedEmail,
            full_name: trimmedName,
            password: optionalPwd,
          },
        });
      } else {
        // billing
        response = await apiRequest({
          endpoint: `/schools/${schoolId}/billing-admins`,
          method: 'POST',
          data: {
            email: trimmedEmail,
            full_name: trimmedName,
            password: optionalPwd,
          },
        });
        // Optional Stripe checkout follow-up
        if (response?.checkout_url) {
          const shouldOpen = await confirmDialog(
            config.successTitle,
            t('invitePerson.billing.openCheckoutPrompt'),
            t('invitePerson.billing.openCheckout'),
            t('common.cancel'),
          );
          if (shouldOpen) {
            await openBillingCheckout(response.checkout_url);
          }
        }
      }

      const successMsg = withInviteWarning(t, config.successMessage, response?.warning);
      // A dialog, not a toast: the message can carry an invite warning.
      showDialog(successMsg);

      onInvited?.(role, response);
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          config.errorMessage,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(15, 23, 42, 0.55)',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <View
          style={{
            width: '100%',
            maxWidth: 880,
            maxHeight: '94%',
            backgroundColor: ADMIN_COLORS.background,
            borderRadius: 22,
            borderWidth: 1,
            borderColor: ADMIN_COLORS.border,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 14,
              padding: 22,
              borderBottomWidth: 1,
              borderBottomColor: ADMIN_COLORS.borderSoft,
            }}
          >
            <View
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                backgroundColor: ADMIN_COLORS.primarySoft,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MaterialIcons name="group-add" size={24} color={ADMIN_COLORS.primary} />
            </View>
            <View style={{ flex: 1 }}>
              <Text
                className="text-xl font-bold"
                style={{ color: ADMIN_COLORS.text.primary }}
              >
                {t('invitePerson.title')}
              </Text>
              <Text
                className="text-sm"
                style={{ color: ADMIN_COLORS.text.secondary, marginTop: 2 }}
              >
                {t(
                  'invitePerson.subtitle',
                )}
              </Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              hitSlop={8}
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: ADMIN_COLORS.surface,
              }}
            >
              <MaterialIcons name="close" size={18} color={ADMIN_COLORS.text.secondary} />
            </TouchableOpacity>
          </View>

          {/* Role tabs (hidden when only one role is allowed — caller is
              opening the modal in single-purpose mode). */}
          {visibleRoles.length > 1 ? (
          <View
            style={{
              flexDirection: 'row',
              gap: 8,
              paddingHorizontal: 22,
              paddingTop: 16,
              paddingBottom: 4,
              flexWrap: 'wrap',
            }}
          >
            {visibleRoles.map((r) => {
              const c = ROLE_CONFIGS[r];
              const active = r === role;
              return (
                <TouchableOpacity
                  key={r}
                  onPress={() => {
                    setRole(r);
                    setError(null);
                  }}
                  style={{
                    flex: 1,
                    minWidth: 140,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    borderRadius: 12,
                    backgroundColor: active
                      ? ADMIN_COLORS.primary
                      : ADMIN_COLORS.surface,
                    borderWidth: 1,
                    borderColor: active
                      ? ADMIN_COLORS.primary
                      : ADMIN_COLORS.border,
                  }}
                >
                  <MaterialIcons
                    name={c.icon}
                    size={16}
                    color={active ? '#FFFFFF' : ADMIN_COLORS.text.secondary}
                  />
                  <Text
                    style={{
                      fontSize: 13,
                      fontWeight: '600',
                      color: active ? '#FFFFFF' : ADMIN_COLORS.text.secondary,
                    }}
                  >
                    {c.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          ) : null}

          {/* Body */}
          <ScrollView
            contentContainerStyle={{ padding: 22, gap: 18 }}
            showsVerticalScrollIndicator={false}
          >
            <View style={{ flexDirection: 'row', gap: 18, flexWrap: 'wrap' }}>
              {/* Form column */}
              <View style={{ flex: 1.4, minWidth: 280, gap: 14 }}>
                <View style={{ gap: 6 }}>
                  <Text
                    className="text-sm font-semibold"
                    style={{ color: ADMIN_COLORS.text.primary }}
                  >
                    {t('invitePerson.fields.name')}
                  </Text>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 10,
                      paddingHorizontal: 12,
                      backgroundColor: ADMIN_COLORS.surface,
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: nameFocused
                        ? ADMIN_COLORS.primary
                        : ADMIN_COLORS.border,
                    }}
                  >
                    <MaterialIcons
                      name="person-outline"
                      size={18}
                      color={ADMIN_COLORS.text.muted}
                    />
                    <TextInput
                      value={fullName}
                      onChangeText={setFullName}
                      onFocus={() => setNameFocused(true)}
                      onBlur={() => setNameFocused(false)}
                      placeholder={t(
                        'invitePerson.fields.namePlaceholder',
                      )}
                      placeholderTextColor={ADMIN_COLORS.text.muted}
                      autoCapitalize="words"
                      editable={!loading}
                      style={{
                        flex: 1,
                        paddingVertical: 12,
                        fontSize: 14,
                        color: ADMIN_COLORS.text.primary,
                      }}
                    />
                  </View>
                </View>

                <View style={{ gap: 6 }}>
                  <Text
                    className="text-sm font-semibold"
                    style={{ color: ADMIN_COLORS.text.primary }}
                  >
                    {t('invitePerson.fields.email')}
                  </Text>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 10,
                      paddingHorizontal: 12,
                      backgroundColor: ADMIN_COLORS.surface,
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: !emailValid
                        ? ADMIN_COLORS.error
                        : emailFocused
                        ? ADMIN_COLORS.primary
                        : ADMIN_COLORS.border,
                    }}
                  >
                    <MaterialIcons
                      name="mail-outline"
                      size={18}
                      color={ADMIN_COLORS.text.muted}
                    />
                    <TextInput
                      value={email}
                      onChangeText={setEmail}
                      onFocus={() => setEmailFocused(true)}
                      onBlur={() => setEmailFocused(false)}
                      placeholder={t(
                        'invitePerson.fields.emailPlaceholder',
                      )}
                      placeholderTextColor={ADMIN_COLORS.text.muted}
                      autoCapitalize="none"
                      keyboardType="email-address"
                      autoComplete="email"
                      editable={!loading}
                      style={{
                        flex: 1,
                        paddingVertical: 12,
                        fontSize: 14,
                        color: ADMIN_COLORS.text.primary,
                      }}
                    />
                  </View>
                  {!emailValid ? (
                    <Text
                      className="text-xs"
                      style={{ color: ADMIN_COLORS.errorText, marginTop: 2 }}
                    >
                      {t(
                        'invitePerson.validation.emailInvalid',
                      )}
                    </Text>
                  ) : null}
                </View>

                {config.passwordEnabled ? (
                  <View style={{ gap: 6 }}>
                    <TouchableOpacity
                      onPress={() => setShowPassword((v) => !v)}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingHorizontal: 12,
                        paddingVertical: 11,
                        borderRadius: 12,
                        backgroundColor: ADMIN_COLORS.surface,
                        borderWidth: 1,
                        borderColor: ADMIN_COLORS.border,
                      }}
                    >
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <MaterialIcons
                          name="lock-outline"
                          size={18}
                          color={ADMIN_COLORS.text.muted}
                        />
                        <Text
                          className="text-sm"
                          style={{ color: ADMIN_COLORS.text.primary }}
                        >
                          {t(
                            'invitePerson.fields.password',
                          )}
                        </Text>
                      </View>
                      <MaterialIcons
                        name={showPassword ? 'expand-less' : 'expand-more'}
                        size={18}
                        color={ADMIN_COLORS.text.secondary}
                      />
                    </TouchableOpacity>
                    {showPassword ? (
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 10,
                          paddingHorizontal: 12,
                          backgroundColor: ADMIN_COLORS.surface,
                          borderRadius: 12,
                          borderWidth: 1,
                          borderColor: pwdFocused
                            ? ADMIN_COLORS.primary
                            : ADMIN_COLORS.border,
                        }}
                      >
                        <MaterialIcons
                          name="vpn-key"
                          size={18}
                          color={ADMIN_COLORS.text.muted}
                        />
                        <TextInput
                          value={password}
                          onChangeText={setPassword}
                          onFocus={() => setPwdFocused(true)}
                          onBlur={() => setPwdFocused(false)}
                          placeholder={t(
                            'invitePerson.fields.passwordPlaceholder',
                          )}
                          placeholderTextColor={ADMIN_COLORS.text.muted}
                          secureTextEntry={pwdSecure}
                          editable={!loading}
                          style={{
                            flex: 1,
                            paddingVertical: 12,
                            fontSize: 14,
                            color: ADMIN_COLORS.text.primary,
                          }}
                        />
                        <TouchableOpacity
                          onPress={() => setPwdSecure((v) => !v)}
                          hitSlop={6}
                        >
                          <MaterialIcons
                            name={pwdSecure ? 'visibility-off' : 'visibility'}
                            size={18}
                            color={ADMIN_COLORS.text.muted}
                          />
                        </TouchableOpacity>
                      </View>
                    ) : null}
                    <Text
                      className="text-xs"
                      style={{ color: ADMIN_COLORS.text.muted }}
                    >
                      {t(
                        'invitePerson.fields.passwordHint',
                      )}
                    </Text>
                  </View>
                ) : null}

                {error ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 12,
                      borderRadius: 12,
                      backgroundColor: ADMIN_COLORS.errorSoft,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.errorBorder,
                    }}
                  >
                    <MaterialIcons
                      name="error-outline"
                      size={18}
                      color={ADMIN_COLORS.errorText}
                    />
                    <Text
                      className="text-sm flex-1"
                      style={{ color: ADMIN_COLORS.errorText }}
                    >
                      {error}
                    </Text>
                  </View>
                ) : null}
              </View>

              {/* Capability sidebar */}
              <View
                style={{
                  flex: 1,
                  minWidth: 240,
                  padding: 18,
                  borderRadius: 16,
                  backgroundColor: ADMIN_COLORS.primaryFaint,
                  borderWidth: 1,
                  borderColor: ADMIN_COLORS.primaryFaintBorder,
                  gap: 14,
                }}
              >
                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
                >
                  <View
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      backgroundColor: ADMIN_COLORS.primarySoft,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <MaterialIcons
                      name={config.capabilityIcon}
                      size={16}
                      color={ADMIN_COLORS.primary}
                    />
                  </View>
                  <Text
                    className="text-sm font-bold"
                    style={{ color: ADMIN_COLORS.text.primary, flex: 1 }}
                  >
                    {config.capabilityTitle}
                  </Text>
                </View>

                {config.capabilities.map((line) => (
                  <View
                    key={line}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <MaterialIcons
                      name="check-circle"
                      size={16}
                      color={ADMIN_COLORS.primary}
                    />
                    <Text
                      className="text-sm flex-1"
                      style={{ color: ADMIN_COLORS.text.primary }}
                    >
                      {line}
                    </Text>
                  </View>
                ))}

                <View
                  style={{
                    marginTop: 4,
                    padding: 12,
                    borderRadius: 10,
                    backgroundColor: ADMIN_COLORS.background,
                    borderWidth: 1,
                    borderColor: ADMIN_COLORS.borderSoft,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <MaterialIcons
                      name="info-outline"
                      size={14}
                      color={ADMIN_COLORS.text.secondary}
                    />
                    <Text
                      className="text-xs font-semibold"
                      style={{ color: ADMIN_COLORS.text.secondary }}
                    >
                      {config.roleHintTitle}
                    </Text>
                  </View>
                  <Text
                    className="text-xs"
                    style={{
                      color: ADMIN_COLORS.text.secondary,
                      marginTop: 6,
                    }}
                  >
                    {config.roleHintBody}
                  </Text>
                </View>
              </View>
            </View>
          </ScrollView>

          {/* Footer */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              padding: 18,
              borderTopWidth: 1,
              borderTopColor: ADMIN_COLORS.borderSoft,
              backgroundColor: ADMIN_COLORS.surface,
              flexWrap: 'wrap',
            }}
          >
            <View
              style={{
                flex: 1,
                minWidth: 220,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <View
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: ADMIN_COLORS.primaryFaint,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MaterialIcons
                  name="verified-user"
                  size={16}
                  color={ADMIN_COLORS.primary}
                />
              </View>
              <Text
                className="text-xs"
                style={{ color: ADMIN_COLORS.text.secondary, flex: 1 }}
              >
                {config.footerHint}
              </Text>
            </View>

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <TouchableOpacity
                onPress={onClose}
                disabled={loading}
                style={{
                  paddingHorizontal: 18,
                  paddingVertical: 11,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: ADMIN_COLORS.border,
                  backgroundColor: ADMIN_COLORS.background,
                  opacity: loading ? 0.5 : 1,
                }}
              >
                <Text
                  className="text-sm font-semibold"
                  style={{ color: ADMIN_COLORS.text.primary }}
                >
                  {t('invitePerson.cancel')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                disabled={loading}
                onPress={submit}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingHorizontal: 20,
                  paddingVertical: 11,
                  borderRadius: 12,
                  backgroundColor: loading ? '#93C5FD' : ADMIN_COLORS.primary,
                }}
              >
                {loading ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <MaterialIcons name={config.submitIcon} size={16} color="#FFFFFF" />
                )}
                <Text className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>
                  {loading ? config.submittingLabel : config.submitLabel}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}
